import { useEffect, useRef, useState } from "react";
import { api } from "./api";

/**
 * TUT-296a — o hook fininho que abre o canal SSE do chat (`/api/realtime/stream`
 * ou `/api/portal/realtime/stream`) e reconecta sozinho. A TELA (296b) decide
 * o que fazer com cada evento — este hook só entrega `chat.message` e cuida
 * da conexão.
 *
 * `EventSource` reconecta sozinho por padrão (é a semântica da API), mas o
 * atraso dele é fixo e curto; o backoff aqui existe para não martelar a API
 * quando ela está fora por um redeploy — o mesmo cuidado de
 * `reportarSuspeitaDeQueda` em `api.ts`, sem duplicar aquele módulo.
 */

export interface ChatStreamEvent {
  type: string;
  data: unknown;
}

export type ChatStreamStatus = "connecting" | "open" | "closed";

const BACKOFF_INICIAL_MS = 1_000;
const BACKOFF_MAXIMO_MS = 30_000;

// TUT-296b — `tools/run-e2e.mjs` serve o web por `vite preview`, sem API
// atrás; um EventSource ali martelaria erro/backoff pra sempre sem nunca
// abrir. Em demo o hook não conecta e fica sempre "closed" — a TELA já
// precisa tolerar isso (é o mesmo estado de uma queda real).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

/**
 * TUT-395 — cada evento vai para `aoEvento`, na hora, um por um. Antes o hook
 * guardava só o ÚLTIMO num estado do React: dois `chat.message` na mesma
 * rodada (duas conversas escrevendo juntas, ou a rajada que chega depois de
 * uma reconexão) viravam UM render, e o primeiro sumia sem ninguém ver.
 * A função mora num ref, então quem chama pode passar uma nova a cada render
 * sem reabrir o canal.
 */
export function useChatStream(
  portal: boolean,
  aoEvento: (evento: ChatStreamEvent) => void,
) {
  const [status, setStatus] = useState<ChatStreamStatus>(
    useDemoApi ? "closed" : "connecting",
  );
  const aoEventoRef = useRef(aoEvento);
  aoEventoRef.current = aoEvento;
  const backoffRef = useRef(BACKOFF_INICIAL_MS);

  useEffect(() => {
    if (useDemoApi) return;
    let fechado = false;
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function conectar() {
      if (fechado) return;
      setStatus("connecting");
      const path = portal ? "/api/portal/realtime/stream" : "/api/realtime/stream";
      source = new EventSource(path, { withCredentials: true });

      source.addEventListener("open", () => {
        backoffRef.current = BACKOFF_INICIAL_MS;
        setStatus("open");
      });

      source.addEventListener("chat.message", (event) => {
        const mensagem = event as MessageEvent<string>;
        aoEventoRef.current({
          type: "chat.message",
          data: JSON.parse(mensagem.data),
        });
      });

      source.addEventListener("heartbeat", () => {
        // Só a prova de vida — a tela não precisa reagir a ela.
      });

      source.addEventListener("error", () => {
        source?.close();
        setStatus("closed");
        if (fechado) return;
        // TUT-391: o servidor agora FECHA o canal quando a sessão cai
        // (desativação, portal revogado, expiração) — e o `EventSource` não
        // distingue isso de uma queda de rede. Sem esta sonda, a aba ficaria
        // reconectando com backoff para sempre, tomando 401 no handshake. A
        // sonda passa pelo `request()`, que num 401 devolve a pessoa ao login
        // (TUT-385) — e o portão desmonta este hook, encerrando o ciclo.
        void api.session().catch(() => undefined);
        const atraso = backoffRef.current;
        backoffRef.current = Math.min(atraso * 2, BACKOFF_MAXIMO_MS);
        timer = setTimeout(conectar, atraso);
      });
    }

    conectar();

    return () => {
      fechado = true;
      if (timer) clearTimeout(timer);
      source?.close();
    };
  }, [portal]);

  return { status };
}
